<template>
  <n-card title="Savings Projections">
    <n-space vertical size="small">
      <n-grid :cols="2" :x-gap="12">
        <n-grid-item v-for="account in savingAccounts" :key="account.id">
          <n-card size="small" :title="account.name">
            <n-form inline :size="small">
              <n-space justify="space-between" align="center">
                <n-form-item label="Monthly €">
                  <n-input-number 
                    v-model:value="accountConfigs[account.id].monthly_contribution"
                    :min="0"
                    :precision="2"
                    size="small"
                    style="width: 120px"
                  />
                </n-form-item>
                <n-form-item label="Interest %">
                  <n-input-number 
                    v-model:value="accountConfigs[account.id].interest_rate"
                    :min="0"
                    :max="100"
                    :precision="2"
                    size="small"
                    style="width: 100px"
                  />
                </n-form-item>
                <n-button 
                  type="primary"
                  size="small"
                  @click="runSimulation(account)"
                >
                  Run
                </n-button>
              </n-space>
            </n-form>
          </n-card>
        </n-grid-item>
      </n-grid>
      
      <div style="height: 400px">
        <Line
          v-if="chartData"
          :data="chartData"
          :options="chartOptions"
        />
      </div>
    </n-space>
  </n-card>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Line } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { useAccountStore } from '../stores/accountStore';
import { useSavingsStore } from '../stores/savingsStore';
import { useRoute } from 'vue-router';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const accountStore = useAccountStore();
const savingsStore = useSavingsStore();

const savingAccounts = computed(() => 
  accountStore.accounts.filter(acc => acc.type === 'Saving')
);

// Configuration state for each account
const accountConfigs = ref({});

// Initialize configs when accounts change
watch(savingAccounts, (accounts) => {
  accounts.forEach(account => {
    if (!accountConfigs.value[account.id]) {
      accountConfigs.value[account.id] = {
        monthly_contribution: account.monthly_contribution || 0,
        interest_rate: account.interest_rate || 0
      };
    }
  });
}, { immediate: true });

const runSimulation = (account) => {
  const config = accountConfigs.value[account.id];
  savingsStore.calculateProjection(
    {
      ...account,
      interest_rate: config.interest_rate
    }, 
    20,
    config.monthly_contribution
  );
};

const chartData = computed(() => {
  if (!savingAccounts.value.length) return null;

  const years = Array.from({ length: 21 }, (_, i) => `Year ${i}`);
  
  return {
    labels: years,
    datasets: savingAccounts.value.map(account => {
      const projection = savingsStore.projections[account.id];
      if (!projection) return null;

      // Get yearly values (every 12th value)
      const values = projection.balances.filter((_, i) => i % 12 === 0);
      
      // Add initial value if needed
      if (values.length && values[0] !== account.last_snapshot?.balance) {
        values.unshift(account.last_snapshot?.balance || 0);
      }

      return {
        label: account.name,
        data: values,
        fill: false,
        borderColor: `hsl(${Math.random() * 360}, 70%, 50%)`,
        tension: 0.1
      };
    }).filter(Boolean) // Remove null datasets
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          return `${label}: ${new Intl.NumberFormat('default', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 2
          }).format(value)}`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      // Remove stacked configuration
      ticks: {
        callback: (value) => {
          return new Intl.NumberFormat('default', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0
          }).format(value);
        }
      }
    }
  }
};

onMounted(async () => {
  await accountStore.fetchAccounts();
  
  // Run initial projections
  savingAccounts.value.forEach(account => {
    if (account.interest_rate) {
      runSimulation(account);
    }
  });
});

// Add a watch to recalculate projections when interest rates change
watch(savingAccounts, (newAccounts) => {
  newAccounts.forEach(account => {
    if (account.interest_rate !== undefined) {
      savingsStore.calculateProjection(
        account,
        20,
        account.monthly_contribution || 0
      );
    }
  });
}, { deep: true });

// Add watch for tab changes
const route = useRoute();
watch(() => route.hash, (newHash) => {
  if (newHash === '#projections') {
    savingAccounts.value.forEach(account => {
      if (account.interest_rate !== undefined) {
        runSimulation(account);
      }
    });
  }
}, { immediate: true });
</script>